<script setup>
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
</script>

<template>
  <!-- eslint-disable vue/no-static-inline-styles -->
  <div class="px-3 p-2">
    <div class="space-y-6">
      <!-- Table skeleton -->
      <div
        class="rounded-md bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
      >
        <!-- Header row -->
        <div
          class="bg-green-200 dark:bg-green-900/20 flex border-b border-green-900/30 dark:border-green-800"
        >
          <div
            v-for="i in 6"
            :key="i"
            class="flex-1 p-4 border-r border-green-900/20 last:border-r-0"
          >
            <div
              class="h-4 bg-gradient-to-r from-green-700 via-green-600 to-green-700 rounded w-20"
              style="background-size: 200% 100%; animation: shimmer 2s infinite"
            />
          </div>
        </div>

        <!-- Body rows -->
        <div class="divide-y divide-slate-200 dark:divide-slate-700">
          <div v-for="row in 8" :key="row" class="flex">
            <div
              v-for="col in 6"
              :key="col"
              class="flex-1 p-3 border-r border-slate-200 dark:border-slate-700 last:border-r-0"
            >
              <!-- Name cell -->
              <div v-if="col === 1" class="flex items-center gap-2">
                <div
                  class="w-4 h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded"
                  :style="`background-size: 200% 100%; animation: shimmer 2s infinite; animation-delay: ${(row % 3) * 0.2}s`"
                />
                <div
                  class="h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded w-24"
                  :style="`background-size: 200% 100%; animation: shimmer 2s infinite; animation-delay: ${(row % 3) * 0.2}s`"
                />
              </div>
              <!-- Category / Language / Inbox cells -->
              <div
                v-else-if="col === 2 || col === 4 || col === 5"
                class="flex items-center gap-2"
              >
                <div
                  class="w-4 h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded"
                  :style="`background-size: 200% 100%; animation: shimmer 2s infinite; animation-delay: ${(row % 3) * 0.2}s`"
                />
                <div
                  class="h-3 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded w-16"
                  :style="`background-size: 200% 100%; animation: shimmer 2s infinite; animation-delay: ${(row % 3) * 0.2}s`"
                />
              </div>
              <!-- Status badge -->
              <div v-else-if="col === 3">
                <div
                  class="h-6 bg-gradient-to-r from-green-200 via-green-300 to-green-200 dark:from-green-700 dark:via-green-600 dark:to-green-700 rounded-full w-20"
                  :style="`background-size: 200% 100%; animation: shimmer 2s infinite; animation-delay: ${(row % 3) * 0.2}s`"
                />
              </div>
              <!-- Action buttons -->
              <div v-else class="flex items-center gap-2">
                <div
                  v-for="b in 2"
                  :key="b"
                  class="w-8 h-8 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded"
                  :style="`background-size: 200% 100%; animation: shimmer 2s infinite; animation-delay: ${(row % 3) * 0.2}s`"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination skeleton -->
        <div
          class="border-t border-green-100 dark:border-green-800 p-4 bg-green-200 dark:bg-green-900/10 flex items-center justify-between"
        >
          <div class="flex-1">
            <div
              class="h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded w-48"
              style="background-size: 200% 100%; animation: shimmer 2s infinite"
            />
          </div>
          <div class="flex gap-2">
            <div
              v-for="i in 5"
              :key="i"
              class="h-8 w-16 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded"
              style="background-size: 200% 100%; animation: shimmer 2s infinite"
            />
          </div>
          <div class="ml-4">
            <div
              class="h-8 w-32 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded"
              style="background-size: 200% 100%; animation: shimmer 2s infinite"
            />
          </div>
        </div>
      </div>

      <!-- Loading status indicator -->
      <div
        class="flex items-center justify-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700"
      >
        <div
          class="w-2 h-2 bg-green-500 rounded-full"
          style="animation: pulse 1.5s infinite"
        />
        <span class="text-sm text-slate-600 dark:text-slate-400 font-medium">
          {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.LOADING') }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
